import { useState, useEffect, ReactNode } from "react";
import { Check } from "lucide-react";
import { Business } from "../../../api/generated";
import { useInvitationContext } from "../context/InvitationContext";
import { AccessTypeControl } from "./AccessTypeControl";
import { EstateSearchControl } from "./EstateSearchControl";
import { EstateGroupFirstControl } from "./EstateGroupFirstControl";
import { HowLongControl } from "./HowLongControl";
import { ServiceSelectControl } from "./ServiceSelectControl";
import { EventSelectControl } from "./EventSelectControl";
import { mayGiveEventPass, type EventPassChoice } from "../eventPass";
import { ServiceBookingSelector } from "./ServiceBookingSelector";
import { isPassFeeService, type ServiceBookingSelection } from "./serviceBooking";
import { evaluateServiceDateReadiness, MULTI_DAY_UNSUPPORTED_REASON, ONE_DAY_NOT_READY_REASON } from "./serviceDateReadiness";
import { useT, type MessageKey, type TFunction } from "../../../i18n/admin";
import { fetchAuthSession } from "../../../utils/session";
import { defaultInstance } from "../../../api/httpClient";
import { useQuery } from "@tanstack/react-query";
import { useBusinessContext } from "../../../context/BusinessContext";
import { useUserPermissions } from "../../../hooks/useUserPermissions";
import { offersMultipleDays } from "../multiDayRule";
import { usePropertyAdminAuthorizedEstates } from "../../../hooks/usePropertyAdminAPI";
import type { Service } from "../../../hooks/useServices";
import { StepHeader } from "../../../components/ui/StepHeader";
import { Notice } from "../../../components/ui/Notice";
import { Button } from "../../../components/ui/Button";
import { businessKindAction, picksPassBusiness, resolveInvitationBusinessId, seesAllPassKinds } from '../invitationBusiness';
import { useIsPhone } from '../../../hooks/useIsPhone';
import {
  PhoneDateRows, PhoneGuestsStep, PhoneStepShell, PhoneTypeStep, dayFromToday, dayOfIso, isoOfDay, type PhoneDateValue,
} from './AccessFormPhone';

// SAM-199: ported off baseui/styletron — this file no longer imports
// `baseui`. Layout & typography now come from Tailwind + the shared DS
// tokens/classes (tokens.css, global.css .fchip/.notice/.step-head), so this
// step reads like the rest of the redesigned flow instead of its own look.
//
// SAM-199 SECOND PASS — reorder (design-access-shots after/05, after/08): the
// owner found the Service section rendering BELOW "How long?"'s own "Next"
// button, so confirming date+guests skipped straight to Guests & Scan without
// the operator ever seeing it. What is being booked (product vs service, and
// WHICH one) is now chosen before "How long?" — availability depends on the
// service, so the service must be known before the date is confirmed. Only
// the parts of the service booking that genuinely need the date — time slot +
// quantities — still render after "How long?", gated behind its own Continue
// button so the flow can't skip past them. `selectedKind`, `bookingKind` and
// `selectedService` moved from local useState into InvitationContext so
// "Back" (which unmounts this component) no longer loses them — see
// InvitationContext.tsx.

const DoneMark = () => (
  <span
    className="inline-flex items-center justify-center rounded-full"
    style={{ width: 16, height: 16, background: "var(--ok-bg)", color: "var(--ok-fg)" }}
  >
    <Check size={11} strokeWidth={3} />
  </span>
);

const FieldTitle = ({ children, done }: { children: ReactNode; done?: boolean }) => (
  <h3 className="flex items-center gap-2 mb-4" style={{ fontSize: 14, fontWeight: 600, color: "var(--ink-900)" }}>
    {children}
    {done && <DoneMark />}
  </h3>
);

/** One filter-chip toggle group — used for access kind and product/service kind. */
const ChipToggle = ({
  options,
  value,
  onChange,
}: {
  options: { label: string; value: string; disabled?: boolean }[];
  value: string;
  onChange: (value: string) => void;
}) => (
  <div className="flex flex-wrap gap-2 mb-6">
    {options.map((opt) => (
      <button
        key={opt.value}
        type="button"
        className={`fchip${value === opt.value ? " on" : ""}`}
        disabled={opt.disabled}
        onClick={() => !opt.disabled && onChange(opt.value)}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

// SAM-683: serviceDateReadiness words its reasons in English (its tests pin them); translate at render.
const translateReadiness = (reason: string | null, t: TFunction, lang: string): string | null => {
  if (!reason || lang === "en") return reason;
  if (reason === ONE_DAY_NOT_READY_REASON) return t("booking.readiness.oneDayNotReady");
  if (reason === MULTI_DAY_UNSUPPORTED_REASON) return t("booking.readiness.multiDay");
  const m = /\(this service (allows up to|requires at least) (\d+) day/.exec(reason);
  if (reason.startsWith(MULTI_DAY_UNSUPPORTED_REASON) && m) {
    const n = Number(m[2]);
    const max = m[1] === "allows up to";
    return `${t("booking.readiness.multiDay")} ${t(max ? "booking.readiness.maxDays" : "booking.readiness.minDays", { n })}`;
  }
  return reason;
};

const ACCESS_KIND_LABEL: Record<string, MessageKey> = {
  residential: "booking.kind.residential",
  business: "booking.kind.business",
  event: "booking.kind.event",
};

const isValidPhoneNumber = (phone: string | undefined): boolean => {
  if (!phone) return false;
  const digits = phone.replace(/\D/g, '');
  return digits.length >= 10 && digits.length <= 15;
};

interface BusinessResponse {
  data: Business[];
  total_pages: number;
  page: number;
  total_elements: number;
}

export default function AccessForm({ onComplete, isLoading = false, onExit }: { onComplete?: (companionsData?: { adults: number; childrens: number }) => void; isLoading?: boolean; onExit?: () => void }) {
  const { invitation, updateInvitation } = useInvitationContext();
  const isPhone = useIsPhone();
  const { business } = useBusinessContext();
  const permissions = useUserPermissions();
  const { t, lang } = useT();
  const [currentStep, setCurrentStep] = useState(0);
  const [howLongKey, setHowLongKey] = useState(0);
  // SAM-944: on a phone the form is a wizard (Type, Access, Guests); coming back from a later step lands on Guests.
  const [phoneStep, setPhoneStep] = useState(() => (invitation?.duration ? 2 : 0));
  const [phoneDate, setPhoneDate] = useState<PhoneDateValue>(() => ({
    mode: invitation?.duration?.mode === 'multiple' ? 'multiple' : 'one',
    date: dayOfIso(invitation?.duration?.date) || dayFromToday(0),
    from: dayOfIso(invitation?.duration?.startDate),
    to: dayOfIso(invitation?.duration?.endDate),
  }));
  const [phoneAdults, setPhoneAdults] = useState(invitation?.guests?.companions?.adults ?? 1);
  const [phoneChildren, setPhoneChildren] = useState(invitation?.guests?.companions?.childrens ?? 0);

  const [sessionBusinessId, setBusinessId] = useState<string | null>(null);
  // The tenant Front Desk has no business of its own: the business it picks as destination is the pass's.
  const businessId = resolveInvitationBusinessId({
    isTenantFrontDesk: picksPassBusiness(permissions),
    destination: invitation?.location?.estate_id?.[0] as any,
    selectedBusinessId: sessionBusinessId,
  });
  const [isSelectedDestinationBusiness, setIsSelectedDestinationBusiness] = useState<boolean | null>(null);
  const [localEstateId, setLocalEstateId] = useState<Array<any>>(invitation?.location?.estate_id || []);
  const [localCompanionsData, setLocalCompanionsData] = useState<{ adults: number; childrens: number }>({ adults: 1, childrens: 0 });

  // SAM-199 — Client Booking can sell either a PRODUCT (today's behaviour, unchanged) or a
  // SERVICE with real date/slot/capacity. Kept as two clearly separate UI paths so a product
  // sale looks and behaves exactly as before — this toggle only decides which one renders.
  // SAM-199 second pass: lifted into InvitationContext (was local useState) so it survives
  // AccessForm remounting when the operator navigates back via the progress bar.
  // SAM-1171 (Rodrigo, 29 Oct): products are out of scope, so Client Booking sells services only and the
  // Product/Service toggle is gone. A pass_fee service is sold with a payment link (no slot, no card here).
  const bookingKind = 'service' as const;
  const selectedService: Service | null = invitation?.selectedService ?? null;
  const sellsPassFee = isPassFeeService(selectedService);
  const handleSelectService = (next: Service | null) => {
    // A different (or cleared) service invalidates whatever time/quantities/finalized
    // selection were built against the PREVIOUS one.
    updateInvitation({
      selectedService: next,
      selectedServiceTime: null,
      serviceBooking: null,
      serviceBookingIdempotencyKey: null,
    });
  };

  // NEW: Access kind selection state
  const accessKinds = [
    { label: t(ACCESS_KIND_LABEL.residential), value: "residential" },
    { label: t(ACCESS_KIND_LABEL.business), value: "business" },
    { label: t(ACCESS_KIND_LABEL.event), value: "event" },
  ];
  
  // Admins, all-invitation managers and the estate group roles (SAM H1: their groups are no business_* ones) see every kind;
  // the server scopes the destinations they can pick to their own estate groups.
  // Property managers are residential-only (they manage estates, not businesses).
  const seesAllKinds = seesAllPassKinds(permissions);
  // A business admin gives passes of their own business (Guest, one day).
  const canSeeBusinessKind = seesAllKinds || permissions.isInvitationManager || permissions.isBusinessAdmin;
  const canSeeResidentialKind = seesAllKinds || permissions.isPropertyManager || permissions.isResidentialInvitationManager;
  // SAM-1304: the Event chip is for the tenant admin and the tenant Front Desk; the server answers 403 to the estate
  // group roles, so they do not see it.
  const canSeeEventKind = mayGiveEventPass(permissions);
  const availableAccessKinds = accessKinds.filter(kind => {
    if (kind.value === "event") return canSeeEventKind;
    if (seesAllKinds) return true;
    if (kind.value === "business") return canSeeBusinessKind;
    if (kind.value === "residential") return canSeeResidentialKind;
    return false;
  });

  const defaultKind = seesAllKinds
    ? "residential"
    : (canSeeResidentialKind ? "residential" : "business");
  // SAM-199 second pass: lifted into InvitationContext (was local useState) — see the
  // bookingKind comment above for why.
  const selectedKind = invitation?.accessKind ?? defaultKind;
  const setSelectedKind = (kind: string) => updateInvitation({ accessKind: kind as 'residential' | 'business' | 'event' });

  // Look up DB user id from cognitoId — needed to scope the authorized estates query
  const [propertyAdminUserId, setPropertyAdminUserId] = useState<number | null>(null);
  useEffect(() => {
    if (!permissions.isPropertyManager || !permissions.cognitoId) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await defaultInstance({ method: 'GET', url: `/user?cognitoid=${permissions.cognitoId}&limit=1` });
        const users = (resp as any)?.data?.data ?? (resp as any)?.data ?? [];
        if (!cancelled && users.length > 0) setPropertyAdminUserId(users[0].id);
      } catch (err) {
        console.error('Error looking up property admin user id:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [permissions.isPropertyManager, permissions.cognitoId]);

  // Fetch authorized estates for property managers to scope estate group dropdown
  const { authorizedEstates: pmAuthorizedEstates } = usePropertyAdminAuthorizedEstates(
    propertyAdminUserId ? { user_id: propertyAdminUserId } : undefined
  );
  const allowedEstateIds = permissions.isPropertyManager
    ? pmAuthorizedEstates.filter(e => e.status_id === 2).map(e => e.estate_id)
    : undefined;

  // Check if Client Booking is selected
  const isClientBookingSelected = selectedKind === 'business' && invitation?.accessType?.[0]?.label === 'Client Booking';


  // Debug logs

  // For non-admin users without residential invitation rights, force business mode.
  // Residential-only invitation users default to residential and pick a property.
  useEffect(() => {
    if (permissions.isLoading || seesAllKinds) return;

    // If the user can ONLY see business kind, lock them into it.
    if (canSeeBusinessKind && !canSeeResidentialKind) {
      setSelectedKind("business");

      const sessionBusinessData = getBusinessFromSessionStorage();
      if (sessionBusinessData) {
        const businessDestination = {
          // i18n-ignore: state value
          label: sessionBusinessData.name || "Current Business",
          id: sessionBusinessData.id || businessId || "",
          isBusiness: true
        } as any;

        setLocalEstateId([businessDestination]);
        updateInvitation({
          location: {
            estate_group_id: [{
              // i18n-ignore: state value (location sent to the API), not copy
              label: "Business Location",
              id: "business" as any
            }],
            estate_id: [businessDestination]
          },
          accessType: [{
            // i18n-ignore: access type value, compared against backend data
            label: "Guest",
            id: 1
          }]
        });

        sessionStorage.setItem('invitationEndpointType', 'business_access');
        setIsSelectedDestinationBusiness(true);
      } else if (!invitation?.accessType?.length) {
        updateInvitation({
          accessType: [{
            // i18n-ignore: access type value, compared against backend data
            label: "Guest",
            id: 1
          }]
        });
      }
    } else if (canSeeResidentialKind && !canSeeBusinessKind) {
      // Residential-only invitation user
      setSelectedKind("residential");
      sessionStorage.setItem('invitationEndpointType', 'invitation');
      setIsSelectedDestinationBusiness(false);
    }
  }, [permissions.isLoading, seesAllKinds, canSeeBusinessKind, canSeeResidentialKind, businessId]);



  useEffect(() => {
    const getBusinessIdFromSessionStorage = () => {
      try {
        const selectedBusinessId = sessionStorage.getItem('selectedBusinessId');
        if (selectedBusinessId) {
          setBusinessId(selectedBusinessId);
        }
      } catch (error) {
        console.error("Error getting business ID from sessionStorage:", error);
      }
    };
    getBusinessIdFromSessionStorage();
  }, []);

  // Get business data directly from sessionStorage
  const getBusinessFromSessionStorage = () => {
    try {
      const businessData = sessionStorage.getItem('businessData');
      if (businessData) {
        const parsed = JSON.parse(businessData);
        return parsed;
      }
    } catch (error) {
      console.error("Error parsing business data from sessionStorage:", error);
    }
    return null;
  };



  const { data: businessData } = useQuery<BusinessResponse, Error>({
    queryKey: ['business', businessId],
    queryFn: async () => {
      if (!businessId) throw new Error('Business ID is required');
      const response = await defaultInstance<BusinessResponse>({
        url: '/business',
        method: 'GET',
        params: {
          id: businessId
        }
      });
      return response.data;
    },
    enabled: !!businessId
  });

  // Note: Removed auto-switching logic to keep Residential as default
  // Users can manually select Business if needed



  useEffect(() => {
   
    if (invitation?.location?.estate_id?.[0]) {
      const selectedDestination = invitation.location.estate_id[0];
      // Only check the isBusiness flag, don't rely on ID comparison
      const isBusiness = (selectedDestination as any)?.isBusiness === true;
      
     
      const endpointType = isBusiness ? 'business_access' : 'invitation';
      
      sessionStorage.setItem('invitationEndpointType', endpointType);
      setIsSelectedDestinationBusiness(isBusiness);
    } else if (!seesAllKinds) {
      // Default endpoint depends on the role: residential-only users default to "invitation"
      const endpointType = (canSeeResidentialKind && !canSeeBusinessKind) ? 'invitation' : 'business_access';
      sessionStorage.setItem('invitationEndpointType', endpointType);
    }
  }, [invitation?.location?.estate_id, businessId, seesAllKinds, canSeeBusinessKind, canSeeResidentialKind]);

  const hasDestinationSelected = Boolean(localEstateId && localEstateId.length > 0);

  // SAM-199 follow-up — the SERVICE booking path's date and guest count now come
  // straight off the shared HowLongControl above, instead of asking again.
  // `invitation.duration.date` is only set once the operator confirms a SINGLE
  // day (HowLongControl's "multiple days" mode has no single date, and — per
  // serviceDateReadiness.ts — SAM cannot write a multi-day service booking at
  // all today) — `invitation.guests.companions` is set at the same time (see
  // handleHowLongComplete), so checking `duration.date` alone is enough to
  // know both are ready.
  const serviceDate = invitation?.duration?.date
    ? invitation.duration.date.slice(0, 10)
    : null;
  const serviceAdults = invitation?.guests?.companions?.adults ?? 0;
  const serviceChildren = invitation?.guests?.companions?.childrens ?? 0;
  const serviceDateReadiness = evaluateServiceDateReadiness(
    invitation?.duration
      ? {
          mode: invitation.duration.mode,
          date: invitation.duration.date,
          startDate: invitation.duration.startDate,
          endDate: invitation.duration.endDate,
        } as any
      : null
  );
  const serviceDateAndGuestsReady = serviceDateReadiness.ready;

  const isStepComplete = (step: number): boolean => {
    if (!invitation) return false;
    
    switch (step) {
      case 0:
        // For residential invitations, we need BOTH access type AND a valid estate selection
        // For business invitations, access type is sufficient
        const hasAccessType = Boolean(invitation.accessType?.length);
        
        if (selectedKind === 'residential') {
          // For residential, we need a specific estate (not business)
          const hasValidResidentialDestination = Boolean(
            hasDestinationSelected && 
            invitation?.location?.estate_id?.[0] &&
            !((invitation.location.estate_id[0] as any)?.isBusiness === true)
          );
          
          return hasAccessType && hasValidResidentialDestination;
        } else if (selectedKind === 'event') {
          // An Event pass needs the event itself: its business is the destination
          return hasAccessType && hasDestinationSelected && Boolean(invitation.selectedEvent);
        } else {
          // For business, just need access type and any destination
          return hasAccessType && hasDestinationSelected;
        }
      case 1:
        // Check for single day (date) or multiple days (startDate + endDate)
        return Boolean(
          invitation.duration?.date ||
          (invitation.duration?.startDate && invitation.duration?.endDate)
        );
      case 2: {
        const mainGuest = invitation.guests?.mainGuest;
        return Boolean(mainGuest?.phoneNumber) &&
               isValidPhoneNumber(mainGuest?.phoneNumber);
      }
      default:
        return false;
    }
  };

  const getNextIncompleteStep = () => {
    for (let i = 0; i <= 2; i++) {
      if (!isStepComplete(i)) {
        return i;
      }
    }
    return 2;
  };

  useEffect(() => {
    const nextStep = getNextIncompleteStep();
    if (nextStep !== currentStep) {
      setCurrentStep(nextStep);
    }
  }, [invitation?.accessType, invitation?.location?.estate_id, invitation?.selectedEvent, invitation?.duration, invitation?.guests?.mainGuest, permissions.isAdmin]);


  const setValue = (name: string, value: any) => {
    const newInvitations: any = {};
    newInvitations[name] = value;
    
    
    updateInvitation(newInvitations);
  };



  const handleHowLongComplete = (value: any) => {
    // Don't proceed if no destination is selected
    if (!hasDestinationSelected) {
      return;
    }

    // Set duration value
    setValue("duration", value);

    // Store companions data locally if provided
    if (value.adults !== undefined && value.children !== undefined) {
      setLocalCompanionsData({ adults: value.adults, childrens: value.children });

      // Save to context for other uses, preserving existing guests data
      setValue("guests", {
        ...invitation?.guests,
        companions: {
          adults: value.adults,
          childrens: value.children
        }
      });

      // SAM-199 second pass — for a SERVICE booking, confirming date+guests here does
      // NOT complete this whole step: the operator still has to pick a time slot and
      // see the derived quantities in the section below (it needs `date`, which only
      // exists once this fires). That section's own Continue button calls onComplete
      // once serviceBooking is actually ready. The PRODUCT path — and every
      // non-Client-Booking flow — is unaffected: Next still advances immediately,
      // exactly as before.
      const needsServiceCompletion = isClientBookingSelected && bookingKind === 'service' && !sellsPassFee;
      if (!needsServiceCompletion) {
        onComplete?.({ adults: value.adults, childrens: value.children });
      }
    }
  };



  // Reset access type and destination when selectedKind changes
  useEffect(() => {
    const sessionBusinessData = getBusinessFromSessionStorage();
    const currentBusiness = businessData?.data?.[0] || sessionBusinessData;
    
    
    if (selectedKind === "business") {
      // Only reset for business if not already a business destination
      const currentDestination = invitation?.location?.estate_id?.[0];
      const isCurrentlyBusiness = (currentDestination as any)?.isBusiness === true;
      
      const businessAction = businessKindAction({ isCurrentlyBusiness, hasCurrentBusiness: !!currentBusiness });
      if (businessAction === 'use-current') {
        const businessDestination = {
          // i18n-ignore: state value
          label: currentBusiness.name || "Current Business",
          id: currentBusiness.id || businessId || "",
          isBusiness: true
        } as any;
        
        
        // Set business as selected destination
        setLocalEstateId([businessDestination]);
        setValue("location", {
          estate_group_id: [{
            // i18n-ignore: state value (location sent to the API), not copy
            label: "Business Location",
            id: "business" as any
          }],
          estate_id: [businessDestination]
        });
        
        // Set default access type for business if not already set
        setValue("accessType", [{
          // i18n-ignore: access type value, compared against backend data
          label: "Guest",
          id: 1
        }]);
        
        // Set endpoint type for business access
        sessionStorage.setItem('invitationEndpointType', 'business_access');
        setIsSelectedDestinationBusiness(true);
      } else if (businessAction === 'reset') {
        // Reset if no business data available
        setValue("accessType", []);
        setLocalEstateId([]);
        setValue("location", { estate_group_id: [], estate_id: [] });
      }
    } else if (selectedKind === "residential") {
      // Only reset if currently has business destination or no destination
      const currentDestination = invitation?.location?.estate_id?.[0];
      const isCurrentlyBusiness = (currentDestination as any)?.isBusiness === true;
      const hasNoDestination = !currentDestination;
      
      
      // Set endpoint type for residential invitation
      sessionStorage.setItem('invitationEndpointType', 'invitation');
      setIsSelectedDestinationBusiness(false);
      
      // FIXED: Don't reset if we already have residential data
      if (isCurrentlyBusiness) {
        setValue("accessType", []);
        setLocalEstateId([]);
        setValue("location", { estate_group_id: [], estate_id: [] });
      } else if (hasNoDestination && localEstateId.length === 0) {
        // Don't reset here, let user select
      } else {
      }
    }
  }, [selectedKind, businessData, businessId]);

  const handleKindChange = (kindValue: string) => {
        setSelectedKind(kindValue);

        // SAM-1304: the event's business is the pass's destination only while the kind is Event; leaving the kind
        // (or entering it) starts the destination over, so no event business leaks into a Business pass.
        if (kindValue !== selectedKind && (selectedKind === "event" || kindValue === "event")) {
          setLocalEstateId([]);
          updateInvitation({
            selectedEvent: null,
            location: { estate_group_id: [], estate_id: [] },
            accessType: kindValue === "event" ? [{
              // i18n-ignore: access type value, compared against backend data
              label: "Guest",
              id: 1
            }] : [],
          });
          if (kindValue === "event") {
            sessionStorage.setItem('invitationEndpointType', 'business_access');
            setIsSelectedDestinationBusiness(true);
          }
        }

        // Immediately load business if "business" is selected
        if (kindValue === "business") {
          const sessionBusinessData = getBusinessFromSessionStorage();

          if (sessionBusinessData) {
            const businessDestination = {
              // i18n-ignore: state value
              label: sessionBusinessData.name || "Current Business",
              id: sessionBusinessData.id || businessId || "",
              isBusiness: true
            } as any;


            // Set business as selected destination immediately
            setLocalEstateId([businessDestination]);
            setValue("location", {
              estate_group_id: [{
                // i18n-ignore: state value (location sent to the API), not copy
                label: "Business Location",
                id: "business" as any
              }],
              estate_id: [businessDestination]
            });

            // Set default access type for business
            setValue("accessType", [{
              // i18n-ignore: access type value, compared against backend data
              label: "Guest",
              id: 1
            }]);

            // Set endpoint type for business access
            sessionStorage.setItem('invitationEndpointType', 'business_access');
            setIsSelectedDestinationBusiness(true);
          }
        }
  };

  // SAM-1304: an Event pass is for one event; its business is the destination, so there is no destination picker.
  const eventBlock = selectedKind === 'event' && canSeeEventKind && (
    <div className="mb-8 p-4" style={{ borderRadius: "var(--r-8)", background: "#fff" }}>
      <FieldTitle done={Boolean(invitation?.selectedEvent)}>{t("booking.access.event")}</FieldTitle>
      <EventSelectControl
        value={invitation?.selectedEvent ?? null}
        onChange={(event: EventPassChoice | null) => {
          if (!event) return;
          const destination = {
            // i18n-ignore: state value (the event's name), not copy
            label: event.name,
            id: event.business_id,
            isBusiness: true,
          } as any;
          setLocalEstateId([destination]);
          updateInvitation({
            selectedEvent: event,
            location: {
              estate_group_id: [{
                // i18n-ignore: state value (location sent to the API), not copy
                label: "Business Location",
                id: "business" as any
              }],
              estate_id: [destination]
            },
          });
          sessionStorage.setItem('invitationEndpointType', 'business_access');
          setIsSelectedDestinationBusiness(true);
        }}
      />
    </div>
  );

  const destinationBlock = selectedKind !== 'event' && (seesAllKinds || (selectedKind === 'residential' && canSeeResidentialKind)) && (
      <div className="mb-8 p-4" style={{ borderRadius: "var(--r-8)", background: "#fff" }}>
        <FieldTitle done={Boolean(invitation?.location?.estate_id?.length)}>{t("booking.access.destination")}</FieldTitle>
        <EstateGroupFirstControl
          defaultValue={invitation?.location?.estate_id}
          onChange={(value) => {
            setLocalEstateId(value);
            const selectedEstate = value[0];
            const locationData = {
              estate_group_id: [{
                label: selectedEstate?.estate_group_name || '',
                id: selectedEstate?.estate_group_id || ''
              }],
              estate_id: value
            };
            setValue("location", locationData);

            // Clear product/service selection when destination changes — ensures
            // whichever one is showing is always selected for the CURRENT
            // destination, never one picked for a previous business.
            if (invitation?.product?.length) {
              setValue("product", []);
            }
            if (invitation?.selectedService || invitation?.serviceBooking) {
              updateInvitation({
                selectedService: null,
                selectedServiceTime: null,
                serviceBooking: null,
                serviceBookingIdempotencyKey: null,
              });
            }

          }}
          includeCurrentBusiness={selectedKind === 'business'}
          currentBusinessName={(() => {
            const name = business?.name || businessData?.data?.[0]?.name || getBusinessFromSessionStorage()?.name;
            return name;
          })()}
          currentBusinessId={(() => {
            const id = businessId || getBusinessFromSessionStorage()?.id || '';
            return id;
          })()}
          kind={selectedKind as 'business' | 'residential'}
          allowedEstateIds={allowedEstateIds}
        />
      </div>
  );

  const accessTypeBlock = (
    <div className="mb-8 p-4" style={{ borderRadius: "var(--r-8)", background: "#fff" }}>
      <FieldTitle done={isStepComplete(0)}>{t("booking.access.accessType")}</FieldTitle>
      <AccessTypeControl
        defaultValue={invitation?.accessType}
        onChange={(value) => setValue("accessType", value)}
        forceGuestOnly={!seesAllKinds && selectedKind === 'business'}
        kind={(selectedKind === 'event' ? 'business' : selectedKind) as 'business' | 'residential'}
      />
    </div>
  );

  const productBlock = selectedKind === 'business' && invitation?.accessType?.[0]?.label === 'Client Booking' && (
      <div className="mb-8 p-4" style={{ borderRadius: "var(--r-8)", background: "#fff" }}>
        <FieldTitle done={Boolean(selectedService)}>{t('booking.access.service')}</FieldTitle>

        {!hasDestinationSelected && (
          <Notice variant="info" className="mb-4">{t('booking.access.pickDestinationServices')}</Notice>
        )}
        {hasDestinationSelected && (
          <ServiceSelectControl
            businessId={invitation?.location?.estate_id?.[0]?.id ? String(invitation.location.estate_id[0].id) : (businessId || '')}
            value={selectedService}
            onChange={handleSelectService}
          />
        )}
      </div>
  );

  const timeBlock = selectedKind === 'business' && invitation?.accessType?.[0]?.label === 'Client Booking' && bookingKind === 'service' && selectedService && !sellsPassFee && (
      <div className="mb-8 p-4" style={{ borderRadius: "var(--r-8)", background: "#fff" }}>
        <FieldTitle done={Boolean(invitation?.serviceBooking)}>{t("booking.access.timeAndQuantities")}</FieldTitle>

        {!serviceDateAndGuestsReady && (
          <Notice variant="warn" className="mb-4">{translateReadiness(serviceDateReadiness.reason, t, lang)}</Notice>
        )}
        {serviceDateAndGuestsReady && (
          <>
            <ServiceBookingSelector
              service={selectedService}
              date={serviceDate}
              adults={serviceAdults}
              children={serviceChildren}
              onSelectionChange={(selection: ServiceBookingSelection | null) => {
                // SAM-199: the selection just changed (or was cleared) — any idempotency key
                // generated for a PREVIOUS selection no longer describes this request, so it
                // must not be reused. updateInvitation (not setValue, which only touches one
                // key) so both fields land in the same state update.
                updateInvitation({ serviceBooking: selection, serviceBookingIdempotencyKey: null });
              }}
            />
            {/* SAM-199 second pass — the step's primary action, now correctly BELOW every
                required input instead of above them. handleHowLongComplete deliberately did
                NOT call onComplete for the service path; this is where it finally does,
                with the exact same companions payload it would have used before. */}
            <div className="mt-4">
              <Button
                variant="primary"
                disabled={!invitation?.serviceBooking || isLoading}
                isLoading={isLoading}
                onClick={() => onComplete?.(localCompanionsData)}
              >
                {t("common.continue")}
              </Button>
            </div>
          </>
        )}
      </div>
  );

  // SAM-944 / SAM-1171: New pass at 375 px, steps 1 to 3 (canvas v256). Same state and rules as the desktop form.
  if (isPhone) {
    const exit = () => onExit?.();
    const isClientBooking = isClientBookingSelected;
    const serviceKind = isClientBooking && bookingKind === 'service';
    const adultCap = (invitation?.accessType?.[0] as any)?.max_adults as number | undefined;
    const childCap = (invitation?.accessType?.[0] as any)?.max_children as number | undefined;
    const dayCap = (invitation?.accessType?.[0] as any)?.max_guests_per_day as number | undefined;
    const cap = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n) && n > 0;
    const limitError = cap(adultCap) && phoneAdults > adultCap
      ? t(adultCap === 1 ? 'booking.howLong.maxAdultsOne' : 'booking.howLong.maxAdultsMany', { n: adultCap })
      : typeof childCap === 'number' && Number.isFinite(childCap) && phoneChildren > childCap
        ? t(childCap === 1 ? 'booking.howLong.maxChildrenOne' : 'booking.howLong.maxChildrenMany', { n: childCap })
        : cap(dayCap) && phoneAdults + phoneChildren > dayCap
          ? t('booking.howLong.maxPerDay', { n: dayCap })
          : '';
    const dateReady = phoneDate.mode === 'one' ? Boolean(phoneDate.date) : Boolean(phoneDate.from && phoneDate.to);
    const accessReady = isStepComplete(0) && dateReady && (!serviceKind || Boolean(selectedService));
    const finishGuests = () => {
      if (limitError) return;
      handleHowLongComplete(
        phoneDate.mode === 'one'
          ? { mode: 'one', date: isoOfDay(phoneDate.date), confirmed: true, adults: phoneAdults, children: phoneChildren }
          : { mode: 'multiple', date: undefined, startDate: isoOfDay(phoneDate.from), endDate: isoOfDay(phoneDate.to), confirmed: true, adults: phoneAdults, children: phoneChildren },
      );
      if (serviceKind) setPhoneStep(3);
    };

    if (phoneStep === 0) {
      return (
        <PhoneStepShell step={1} onClose={exit} onContinue={() => setPhoneStep(1)} continueDisabled={!selectedKind || selectedKind === 'event'}>
          <PhoneTypeStep
            options={availableAccessKinds.map((kind) => ({ ...kind, disabled: kind.value === 'event' }))}
            value={selectedKind}
            onChange={handleKindChange}
          />
        </PhoneStepShell>
      );
    }
    if (phoneStep === 1) {
      return (
        <PhoneStepShell step={2} onBack={() => setPhoneStep(0)} onClose={exit} onContinue={() => setPhoneStep(2)} continueDisabled={!accessReady}>
          <div className="flex flex-col gap-3 p-4">
            {destinationBlock}
            {accessTypeBlock}
            {productBlock}
            <PhoneDateRows
              value={phoneDate}
              onChange={setPhoneDate}
              multipleDisabled={serviceKind || invitation?.accessType?.[0]?.label?.toLowerCase() === 'delivery'}
              restrictionReason={serviceKind ? t('booking.access.singleDayReason') : undefined}
            />
          </div>
        </PhoneStepShell>
      );
    }
    if (phoneStep === 2) {
      return (
        <PhoneStepShell step={3} onBack={() => setPhoneStep(1)} onClose={exit} onContinue={finishGuests} continueDisabled={Boolean(limitError)} loading={isLoading}>
          <PhoneGuestsStep
            adults={phoneAdults}
            children={phoneChildren}
            onAdults={setPhoneAdults}
            onChildren={setPhoneChildren}
            maxAdults={cap(adultCap) ? adultCap : undefined}
            maxChildren={typeof childCap === 'number' && Number.isFinite(childCap) ? childCap : undefined}
            maxTotal={cap(dayCap) ? dayCap : undefined}
            error={limitError}
          />
        </PhoneStepShell>
      );
    }
    // A service booking also needs its time and quantities (no phone mock yet: the desktop block, stacked).
    return (
      <PhoneStepShell step={3} onBack={() => setPhoneStep(2)} onClose={exit}>
        <div className="p-4">{timeBlock}</div>
      </PhoneStepShell>
    );
  }

  return (
    <div className="flex gap-8 w-full">
      <div
        className="flex-1 mx-auto w-full"
        style={{ border: "1px solid var(--line)", borderRadius: "var(--r-12)", padding: "2rem", background: "#fff", maxWidth: 640 }}
      >
        <StepHeader eyebrow={t("booking.access.eyebrow")} title={t("booking.access.title")} description={t("booking.access.description")} />

        {/* Access kind — one filter-chip group, same shape everywhere in the app (.fchip). */}
        <ChipToggle
          options={availableAccessKinds}
          value={selectedKind}
          onChange={handleKindChange}
        />

        {eventBlock}

        {/* Destination field first - admins, all-invitation managers, and residential invitation users (need to pick a property) */}
        {destinationBlock}

        {accessTypeBlock}

        {/* Service — WHAT is being booked, and WHICH one, chosen
            BEFORE "How long?" (SAM-199 second pass). Availability depends on
            the service, so the service has to be known before the date is
            confirmed — and the primary action that advances the whole step
            must never sit above a required input, which is exactly what
            happened when this rendered below "How long?"'s own Next button. */}
        {productBlock}

        <div
          className="mb-8 p-4"
          style={{
            borderRadius: "var(--r-8)",
            background: "#fff",
            opacity: !hasDestinationSelected ? 0.5 : 1,
            pointerEvents: !hasDestinationSelected ? 'none' : 'auto'
          }}
        >
          <FieldTitle done={isStepComplete(1)}>{t("booking.access.howLong")}</FieldTitle>
          {!hasDestinationSelected && (
            <Notice variant="info" className="mb-4">{t(selectedKind === 'event' ? "booking.access.pickEvent" : "booking.access.pickDestination")}</Notice>
          )}
          {hasDestinationSelected && (
            <HowLongControl
              key={howLongKey}
              onChange={handleHowLongComplete}
              defaultValue={{ mode: "one", date: undefined }}
              isLoading={isLoading}
              accessType={invitation?.accessType?.[0]?.label}
              maxAdults={(invitation?.accessType?.[0] as any)?.max_adults}
              maxChildren={(invitation?.accessType?.[0] as any)?.max_children}
              maxGuestsPerDay={(invitation?.accessType?.[0] as any)?.max_guests_per_day}
              // SAM-199 follow-up — a SERVICE booking can't use a date range
              // today (serviceDateReadiness.ts). Only restrict once Service is
              // the active booking kind — the PRODUCT path keeps today's
              // behaviour, including multiple days, exactly as before.
              restrictToOneDay={isClientBookingSelected && bookingKind === 'service' && !sellsPassFee}
              restrictionReason={t("booking.access.singleDayReason")}
              hideMultipleDays={!offersMultipleDays({ caller: permissions, kind: selectedKind === 'event' ? 'business' : selectedKind, passTypeLabel: invitation?.accessType?.[0]?.label })}
            />
          )}
        </div>

        {/* Time & quantities — the only parts of a SERVICE booking that
            genuinely need the date, so they render after "How long?" confirms
            it (SAM-199 second pass). A SERVICE booking reuses that control's
            date and Adults/Children total instead of asking for either a
            second time (see ServiceBookingSelector / serviceGuestMapping.ts).
            The PRODUCT path is unaffected — it never reads duration/guests. */}
        {timeBlock}
      </div>
    </div>
  );
}
